<script lang="ts" setup>
import type { GoogleGeocodeQuery } from '#server/routes/server/geocode'

import { CustomControl, CustomMarker, GoogleMap } from 'vue3-google-map'
import mapsStyles from '~/assets/maps-style.json'
import IconPin from '~/components/icon/IconPin.vue'
import TextWithAfterLine from '~/components/text/TextWithAfterLine.vue'

const props = defineProps({
  context: Object,
})
const { $directus, $readItems, $i18n } = useNuxtApp()
const config = useRuntimeConfig()
const { t } = useI18n()

const formKitInput = computed({
  get: () => props.context?._value,
  set: (value) => {
    props.context?.node.input(value)
  },
})

const { data } = await useAsyncData(
  'geocoded_dealers',

  async () => {
    const results = await $directus.request($readItems('dealers', {
      sort: 'name',
      filter: {
        is_visible_on_website: {
          _eq: 'true',
        },
      },
      fields: [
        'id',
        'name',
        'street',
        'number',
        'city',
        'postal_code',
        'country',
        'email',
        'phone_number',
      ],
    })) as {
      id: string
      name: string
      street: string
      number: string
      city: string
      postal_code: string
      country: string
      email: string
      phone_number: string
    }[]

    const promises: Promise<GoogleGeocodeQuery>[] = []
    results.forEach((dealer) => {
      const promise = $fetch('/server/geocode-cache', {
        query: {
          address: `${dealer.street} ${dealer.number}, ${dealer.city}, ${dealer.country}`,
          lang: $i18n.locale.value,
        },
      })

      promises.push(promise)
    })

    let geoResult: PromiseSettledResult<GoogleGeocodeQuery>[] = []

    try {
      geoResult = await Promise.allSettled(promises)
    }
    catch (error) {
      console.error(error)
    }

    return geoResult.map((r, i) => {
      const location = r.status === 'fulfilled' && r.value.results[0]
        ? {
            geometry: r.value.results[0]?.geometry.location,
            address: r.value.results[0]?.formatted_address,
          }
        : null

      return {
        ...results[i],
        location,
      }
    })
  },
)

const activeDealerId = computed({
  get: () => formKitInput.value?.id,
  set: (value) => {
    formKitInput.value = data.value?.find(dealer => dealer.id === value)
  },
})

const mapRef = ref<typeof GoogleMap>()
const scrollContainer = ref<HTMLElement>()

watch([() => mapRef.value?.ready, data], ([ready, data]) => {
  if (!ready)
    return
  if (!data)
    return
  if (data.length === 0)
    return

  if (!mapRef.value?.map)
    return
  const bounds = new mapRef.value.api.LatLngBounds()
  data?.forEach((dealer) => {
    if (!dealer.location)
      return
    bounds.extend(dealer.location.geometry)
  })
  mapRef.value.map.fitBounds(bounds)

  // do something with the api using `mapRef.value.api`
  // or with the map instance using `mapRef.value.map`
})

// const activeDealerId = ref<string | null>()

// const currentDealer = computed(() => {
//   return data.value?.find(dealer => dealer.id === activeDealerId.value)
// })

function scrollActiveItemIntoView() {
  if (!formKitInput.value?.id)
    return
  scrollContainer.value?.querySelector(`[data-id="${formKitInput.value?.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
}

const search = ref<string>('')

const searchedLocation = ref<GoogleGeocodeQuery['results'][number] | null>(null)

async function handleMapSearch() {
  if (!search.value) {
    searchedLocation.value = null
    return
  }
  const results = await $fetch('/server/geocode', {
    query: {
      address: search.value,
      lang: $i18n.locale.value,
    },
  })
  if (results.status !== 'OK') {
    searchedLocation.value = null
    return
  }
  searchedLocation.value = results.results[0] || null
}

const validDealers = computed(() => {
  return data.value?.filter(dealer => dealer.location !== null) || []
})

const sortedDealers = computed(() => {
  if (!searchedLocation.value)
    return validDealers.value

  return validDealers.value.toSorted((a, b) => {
    const aDistance = DistanceBetweenCoords(a.location!.geometry, searchedLocation.value!.geometry.location)
    const bDistance = DistanceBetweenCoords(b.location!.geometry, searchedLocation.value!.geometry.location)
    return aDistance - bDistance
  }).slice(0, 3)
})

watch(formKitInput, () => {
  if (!formKitInput.value?.location)
    return

  const zoom = mapRef.value?.map.getZoom()
  if (zoom < 7)
    mapRef.value?.map.setZoom(7)
  mapRef.value?.map.panTo(formKitInput.value?.location?.geometry)

  if (sortedDealers.value?.findIndex((dealer) => {
    return dealer.id === formKitInput.value?.id
  }) === -1) {
    clearLocationSearch()
  }

  nextTick(() => scrollActiveItemIntoView())
})

watch(searchedLocation, () => {
  if (!searchedLocation.value)
    return

  const bounds = new mapRef.value!.api.LatLngBounds()
  bounds.extend(searchedLocation.value.geometry.location)
  sortedDealers.value?.forEach((dealer) => {
    bounds.extend(dealer.location?.geometry)
  })
  mapRef.value?.map.fitBounds(bounds)
})

function clearLocationSearch() {
  search.value = ''
  searchedLocation.value = null
  nextTick().then(() => {
    scrollActiveItemIntoView()
  })
}
</script>

<template>
  <div class="grid grid-cols-1 gap-5rem lg:grid-cols-[5fr_4fr]">
    <div class="h-650px flex flex-col lg:h-full">
      <TextWithAfterLine
        class="mb-2.5rem"
        :text="t('form.dealers.search_postal_code_and_municipality')"
      />
      <div class="grid grid-cols-[1fr_min-content] mb-2rem gap-1.8rem">
        <FormKit
          v-model="search"
          ignore
          type="text"
          :label="t('form.dealers.postal_code_and_municipality')"
          :placeholder="t('form.dealers.postal_code_and_municipality')"
          @keypress.prevent.enter="handleMapSearch()"
        />
        <button
          class="mt-auto block border border-neutral-white px-2.5rem py-1.25rem text-center text-1.3rem text-neutral-white tracking-4px uppercase transition duration-300 block-light:border-neutral-black block-light:text-neutral-black hover:bg-neutral-white hover:text-neutral-black block-light:hover:bg-neutral-black block-light:hover:text-neutral-white"
          @click="handleMapSearch()"
        >
          {{ t('form.dealers.search') }}
        </button>
      </div>
      <ClientOnly>
        <div class="h-full bg-starline-secondary ring-px ring-neutral-gray/40 transition focus-within:ring-3 focus-within:ring-starline-primary">
          <GoogleMap
            ref="mapRef"
            background-color="none"
            :api-key="config.public.googleMaps.apiKey"
            style="width: 100%; height: 100%"
            :zoom="5"
            :min-zoom="5"
            disable-default-ui
            :styles="mapsStyles"
            :language="$i18n.locale.value"
          >
            <template #default="{ map }">
              <CustomMarker
                v-for="dealer in validDealers"
                :key="dealer.id"
                :options="{
                  position: dealer.location?.geometry ?? { lat: 0, lng: 0 },
                  anchorPoint: 'BOTTOM_CENTER',
                }"
                @click="activeDealerId = dealer.id"
              >
                <div class="relative">
                  <div class="pointer-events-none absolute rounded-full bg-starline-primary/20 blur-xl -inset-1rem" />
                  <IconPin v-if="activeDealerId !== dealer.id" class="relative h-3rem w-3rem text-starline-primary" />
                  <IconPinSelected v-else class="relative h-3rem w-3rem text-starline-primary" />
                </div>
              </CustomMarker>
              <CustomControl position="RIGHT_BOTTOM">
                <div class="m-1rem flex flex-col bg-starline-secondary/50 shadow-starline backdrop-blur">
                  <button class="appearance-none p-1rem hover:bg-white/10" @click="map.setZoom(map.getZoom() + 1)">
                    <div class="i-radix-icons-plus size-1.8rem text-white" />
                  </button>
                  <div class="mx-.6rem h-.5px bg-neutral-white" />
                  <button class="appearance-none p-1rem hover:bg-white/10" @click="map.setZoom(map.getZoom() - 1)">
                    <div class="i-radix-icons-minus size-1.8rem text-white" />
                  </button>
                </div>
              </CustomControl>
              <CustomControl v-if="formKitInput" position="LEFT_BOTTOM">
                <div class="relative m-1rem w-md border-px border-starline-primary bg-neutral-white p-1.5rem text-neutral-black shadow-starline">
                  <div class="grid grid-cols-[1fr_min-content]">
                    <div class="text-1.7rem font-title">
                      {{ formKitInput.name }}
                    </div>
                    <button class="mb-auto block appearance-none p-.5rem -mt-.4rem hover:bg-neutral-black/10" @click="activeDealerId = null">
                      <div class="i-radix-icons-cross-1 size-1.8rem" />
                    </button>
                  </div>

                  <div class="mt-.8rem whitespace-pre-line text-1.6rem text-neutral-gray leading-2.2rem">
                    {{ formKitInput.location?.address.split(',').join('\n') }}
                  </div>
                </div>
              </CustomControl>
            </template>
          </GoogleMap>
        </div>

        <template #fallback>
          <div class="h-full w-full animate-pulse bg-neutral-white/10 block-light:bg-neutral-black/10" />
        </template>
      </ClientOnly>
    </div>

    <div class="h-650px flex flex-col">
      <TextWithAfterLine class="mb-2.5rem" :text="t('form.dealers.select_local_dealer')" />
      <div ref="scrollContainer" v-auto-animate class="h-full flex flex-col gap-1rem overflow-x-visible overflow-y-scroll">
        <label v-for="dealer in sortedDealers" :key="dealer.id" :data-id="dealer.id" class="m-3px cursor-pointer">
          <div
            class="grid grid-cols-[1fr_min-content] gap-1.2rem px-2rem py-1.2rem transition"
            :class="activeDealerId === dealer.id ? 'ring-2 ring-starline-primary' : 'ring-px ring-neutral-gray/40'"
          >
            <div>
              <span class="text-1.7rem font-title">{{ dealer.name }}</span>
              <p class="mt-.8rem whitespace-pre-line text-1.6rem text-neutral-gray leading-2.2rem">
                {{ dealer.location?.address.split(',').join('\n') }}
              </p>
            </div>
            <div
              class="grid my-auto size-1.6rem place-items-center border-px border-neutral-white text-neutral-white block-light:border-neutral-black block-light:text-neutral-black"
            >
              <div v-if="activeDealerId === dealer.id" class="i-radix-icons-check" />
            </div>
          </div>
          <input v-model="activeDealerId" class="hidden" type="radio" :value="dealer.id">
        </label>
        <button
          v-if="searchedLocation"
          class="mx-auto block border border-neutral-white px-1.25rem py-.75rem text-center text-1rem text-neutral-white tracking-4px uppercase transition duration-300 block-light:border-neutral-black block-light:text-neutral-black hover:bg-neutral-white hover:text-neutral-black block-light:hover:bg-neutral-black block-light:hover:text-neutral-white"
          @click="clearLocationSearch()"
        >
          {{ t('form.dealers.show_all_dealers') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.gm-style iframe + div) {
  border: none !important;
}
</style>
